import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Sparkles, Layers, Upload, ArrowRight } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import theme from '../theme';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NftArtStudio({ user }) {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/nft/projects`, { headers: { Authorization: `Bearer ${token}` } });
        setProjects(res.data.projects || []);
      } catch (err) { /* silent */ }
    };
    fetchProjects();
  }, []);

  const tools = [
    {
      icon: Sparkles, title: 'AI Image Generator', desc: 'Generate NFT art from text prompts.',
      path: '/creator/nft-art/ai', color: '#A855F7',
    },
    {
      icon: Layers, title: 'Trait-Based Generator', desc: 'Upload layers, compose unique combinations.',
      path: '/creator/nft-art/traits', color: '#3B82F6',
    },
  ];

  return (
    <div data-testid="nft-art-studio-page">
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{ fontFamily: theme.fonts.heading, fontSize: '28px', fontWeight: '700', color: theme.colors.text.primary, marginBottom: '8px' }}>
          NFT Art Studio
        </h1>
        <p style={{ color: theme.colors.text.secondary, fontSize: '15px' }}>
          Create and manage artwork for your NFT collections.
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '20px', marginBottom: '40px' }}>
        {tools.map((t) => (
          <Card
            key={t.path}
            data-testid={`tool-card-${t.title.toLowerCase().replace(/\s/g, '-')}`}
            onClick={() => navigate(t.path)}
            style={{ border: `1px solid ${theme.colors.borders}`, borderRadius: theme.radius.card, cursor: 'pointer', transition: 'border-color 0.2s, transform 0.2s' }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = t.color; e.currentTarget.style.transform = 'translateY(-2px)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = theme.colors.borders; e.currentTarget.style.transform = 'translateY(0)'; }}
          >
            <CardContent className="pt-6 pb-6">
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: `${t.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <t.icon size={22} color={t.color} />
                </div>
                <div>
                  <p style={{ fontFamily: theme.fonts.heading, fontSize: '16px', fontWeight: '600', color: theme.colors.text.primary }}>{t.title}</p>
                  <p style={{ fontSize: '13px', color: theme.colors.text.muted }}>{t.desc}</p>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: t.color, fontSize: '13px', fontWeight: '500' }}>
                Open <ArrowRight size={14} />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {projects.length > 0 && (
        <>
          <h2 style={{ fontFamily: theme.fonts.heading, fontSize: '18px', fontWeight: '600', color: theme.colors.text.primary, marginBottom: '16px' }}>
            Your Projects
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
            {projects.map((p) => (
              <Card
                key={p.id}
                onClick={() => navigate(`/creator/nft-projects/${p.id}/art`)}
                style={{ border: `1px solid ${theme.colors.borders}`, borderRadius: theme.radius.card, cursor: 'pointer' }}
              >
                <CardContent className="pt-4 pb-4">
                  <p style={{ fontWeight: '600', color: theme.colors.text.primary, fontSize: '14px' }}>{p.name}</p>
                  <p style={{ fontSize: '12px', color: theme.colors.text.muted }}>{p.quantity} items &middot; {p.standard}</p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: theme.colors.primary, fontSize: '12px', marginTop: '8px' }}>
                    Manage Art <ArrowRight size={12} />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
